import { useState } from 'react';
import { supabase } from './supabaseClient';

// Self-serve account deletion (Apple guideline 5.1.1(v) requires it in-app).
// Rendered on both profile surfaces: the /profile page (nav drawer → Profile)
// and the ProfileEdit card. The delete-account edge function re-derives
// identity from the JWT and purges storage before deleting the auth user;
// the typed DELETE gate lives here.
export default function DeleteAccountSection() {
  const [armed, setArmed] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const confirmed = confirmText.trim().toUpperCase() === 'DELETE';

  const handleDelete = async () => {
    if (!confirmed || deleting) return;
    setDeleting(true);
    setError(null);
    const { data, error: fnError } = await supabase.functions.invoke('delete-account', { body: { confirm: true } });
    if (fnError || !data?.success) {
      setError('Deletion failed — nothing was removed. Please try again, or email us from your account address.');
      setDeleting(false);
      return;
    }
    // The session is already dead server-side; signOut just clears it locally.
    try { await supabase.auth.signOut(); } catch (e) { /* already gone */ }
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <div style={S.zone}>
      <label style={S.label}>DANGER ZONE</label>
      {!armed ? (
        <>
          <button style={S.armBtn} onClick={() => setArmed(true)}>DELETE MY ACCOUNT</button>
          <div style={S.hint}>Permanently removes your account, collection, card photos and messages.</div>
        </>
      ) : (
        <>
          <div style={S.warning}>
            This permanently deletes your account, your entire collection, your card photos and your messages. There is no undo. Type DELETE to confirm.
          </div>
          <input style={S.input} value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="Type DELETE" autoFocus />
          {error && <div style={S.errorMsg}>{error}</div>}
          <div style={S.btnRow}>
            <button style={S.keepBtn} onClick={() => { setArmed(false); setConfirmText(''); setError(null); }} disabled={deleting}>KEEP ACCOUNT</button>
            <button
              style={{ ...S.deleteBtn, opacity: confirmed && !deleting ? 1 : 0.4, cursor: confirmed && !deleting ? 'pointer' : 'default' }}
              onClick={handleDelete}
              disabled={deleting || !confirmed}
            >
              {deleting ? 'DELETING...' : 'DELETE FOREVER'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const S = {
  zone: { marginTop: '28px', paddingTop: '20px', borderTop: '1px solid rgba(var(--red-rgb),0.25)' },
  label: { display: 'block', fontFamily: "'Orbitron', monospace", fontSize: '9px', letterSpacing: '2px', color: 'var(--red)', marginBottom: '10px', opacity: 0.85 },
  armBtn: { width: '100%', fontFamily: "'Orbitron', monospace", fontSize: '10px', letterSpacing: '2px', padding: '11px', background: 'transparent', border: '1px solid rgba(var(--red-rgb),0.4)', color: 'var(--red)', cursor: 'pointer', borderRadius: '2px', marginBottom: '6px' },
  hint: { fontFamily: "'Share Tech Mono', monospace", fontSize: 'var(--fs-hint)', color: 'var(--muted-2)', marginTop: '6px' },
  warning: { fontFamily: "'Share Tech Mono', monospace", fontSize: '12px', color: 'var(--red)', lineHeight: 1.6, marginBottom: '12px', background: 'rgba(var(--red-rgb),0.06)', border: '1px solid rgba(var(--red-rgb),0.2)', padding: '10px 14px', borderRadius: '2px' },
  input: { width: '100%', background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: "'Share Tech Mono', monospace", fontSize: '14px', padding: '10px 14px', borderRadius: '2px', outline: 'none', boxSizing: 'border-box' },
  errorMsg: { background: 'rgba(var(--red-rgb),0.1)', border: '1px solid rgba(var(--red-rgb),0.3)', color: 'var(--red)', fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', padding: '10px 14px', borderRadius: '2px', marginTop: '10px', lineHeight: 1.5 },
  btnRow: { display: 'flex', gap: '8px', marginTop: '12px' },
  keepBtn: { flex: 1, fontFamily: "'Orbitron', monospace", fontSize: '11px', letterSpacing: '2px', padding: '13px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted-2)', cursor: 'pointer', borderRadius: '2px' },
  deleteBtn: { flex: 2, fontFamily: "'Orbitron', monospace", fontSize: '11px', letterSpacing: '2px', padding: '13px', background: 'rgba(var(--red-rgb),0.12)', border: '1px solid var(--red)', color: 'var(--red)', borderRadius: '2px' },
};
